import {
  ACCEPTED_EXTENSIONS_LABEL,
  type FileAdmission,
  MAX_DOCS_PER_SESSION,
  MAX_FILE_BYTES,
  MAX_TOTAL_BYTES,
  admitFiles
} from "./admitFiles";
import type { CommittedDocuments } from "@lib/agent/types";
import { notifications } from "@lib/notification/notification";

export * from "./admitFiles";

/**
 * Surface the standard cap/size warnings for an {@link admitFiles} result. Centralized so the
 * warning copy lives in one place wherever files are picked.
 */
export function notifyAdmissionWarnings({
  unsupported,
  oversized,
  droppedBySlots,
  droppedByTotalSize
}: FileAdmission): void {
  if (unsupported.length > 0) {
    notifications.warning(
      "Unsupported file type",
      `${unsupported.map(f => f.name).join(", ")} can't be attached. Accepted types: ${ACCEPTED_EXTENSIONS_LABEL}.`
    );
  }
  if (oversized.length > 0) {
    notifications.warning(
      "File too large",
      `${oversized.map(f => f.name).join(", ")} exceeds the ${(MAX_FILE_BYTES / 1_000_000).toFixed(1)} MB per-file limit.`
    );
  }
  if (droppedBySlots > 0) {
    notifications.warning(
      "Document limit reached",
      `Only ${MAX_DOCS_PER_SESSION} documents can be attached at a time. ${droppedBySlots} file(s) were not added.`
    );
  }
  if (droppedByTotalSize > 0) {
    notifications.warning(
      "Attachment size limit reached",
      `${droppedByTotalSize} file(s) were not added because attachments would exceed the ${MAX_TOTAL_BYTES / 1_000_000} MB total size limit.`
    );
  }
}

/**
 * The subset of `incoming` that can join the draft, given the documents already committed to the
 * session and the files already in the draft. Warns the user about every file left out. Returns
 * only the newly admitted files; the caller appends them to the draft.
 */
export function admitDraftFiles(incoming: File[], draftFiles: File[], committedDocuments: CommittedDocuments): File[] {
  const draftBytes = draftFiles.reduce((sum, f) => sum + f.size, 0);
  const admission = admitFiles(incoming, {
    existingCount: committedDocuments.count + draftFiles.length,
    existingBytes: committedDocuments.totalBytes + draftBytes
  });
  notifyAdmissionWarnings(admission);
  return admission.accepted;
}

/**
 * Build the multipart body for a document upload: one `files` part per document, matching the
 * endpoint's `files` field. The whole draft is sent in a single request so the backend commits the
 * documents atomically — all or none.
 */
export function buildUploadForm(files: File[]): FormData {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file);
  }
  return formData;
}
